import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IMedication, IMedicationOption, IMedicationResult, ISaveMedication } from '../../../../../models/medication.interface';
import { environment } from '../../../../../../environments/environment';
import { catchError, combineLatest, filter, finalize, map, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { ConsultationRecommendationService } from '../../../../../services/consultation-recommendation/consultation-recommendation.service';
import { MedicationService } from '../../../../../services/medication/medication.service';
import { CVANotificationService } from '../../../../../services/cva-notification/cva-notification.service';
import { CVA_CONSTANTS } from '../../../../../util/constants';
import { NotificationClassEnum } from '../../../../../models/notification-class.enum';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  standalone: false,
  selector: 'cva-consultation-medications',
  templateUrl: './consultation-medications.component.html',
  styleUrl: './consultation-medications.component.scss',

})
export class ConsultationMedicationsComponent implements OnInit, OnDestroy {
  @ViewChild('bottom') bottomRef!: ElementRef;
  @Input() patientId: string;
  @Input() practitionerId: string;

  medications: IMedication[] = [];
  cvaUI_url = environment.cvaUI_url;
  showAddForm = false;
  isSaveAttempted = false;
  isLoading = false;

  private readonly _unsubscribeAll = new Subject<void>();

  constructor(
    private readonly cvaNotificationService: CVANotificationService,
    private readonly recommendationService: ConsultationRecommendationService,
    private readonly medicationService: MedicationService
  ) {

  }

  ngOnInit(): void {
    if (environment.RECOMMENDATIONS.MEDICATIONS) {
      this.listenToGenerateMedications();
    }
    this.medicationService.medications$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(medications => this.medications = medications);
  }

  toggleCheckbox(index: number) {
    this.medicationService.toggleItem(index);
  }


  get isSubmitDisabled(): boolean {
    return !this.medications.some(item => item.checked && !item.isSaved);
  }

  trackByIndex(index: number): number {
    return index;
  }


  listenToGenerateMedications() {
    this.recommendationService.generateNotesTrigger$
      .pipe(
        takeUntil(this._unsubscribeAll),
        // Get the current failed state when the trigger happens
        switchMap((payload) =>
          this.recommendationService.generateNotesFailed$.pipe(
            take(1),
            map((failed) => ({ failed, payload }))
          )
        ),
        // Skip API call if parent already failed
        filter(({ failed }) => !failed),
        filter(({ payload }) =>
          payload.isTranscriptionChanged ||
          this.medications.length === 0
        ),
        // Proceed with the API call
        switchMap(({ payload }) => {
          this.isLoading = true;
          return this.medicationService.generateMedications(payload.transcript)
            .pipe(
              finalize(() => {
                this.isLoading = false;   // always runs (success, error, cancel)
              }),
              catchError((err) => {
                console.error('[Child] generateMedications() failed', err);
                this.medicationService.setMedications([], true)
                return of({ response: { prescriptions: [] } })
              })
            );
        }),
        // Wait until parent signals it's ready before using the response     
        switchMap((res) =>
          combineLatest([
            this.recommendationService.generateNotesReady$,
            this.recommendationService.generateNotesFailed$
          ]).pipe(
            filter(([ready, failed]) => ready || failed),
            take(1),
            map(([ready]) => ready ? res : { response: { prescriptions: [] } })
          )
        )
      )
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          const prescriptions = res.response.prescriptions.filter(record => record.options.length > 0)
          this.medicationService.setMedications(prescriptions, true)
        }
      });
  }

  showAddItemForm() {
    this.showAddForm = true;
    setTimeout(() => this.scrollToBottom(), 100);
  }

  truncateString(value: string | undefined, maxLength: number = 80): string {
    if (!value) return '';
    if (value.length <= maxLength) {
      return value;
    }
    return value.slice(0, maxLength) + '...';
  }

  postChecked() {
    this.isSaveAttempted = true;

    const hasInvalidCheckedMedications = this.medications.some(med =>
      med.checked &&
      !med.isSaved &&
      (
        !med.selectedMedicine ||
        !med.dosageInstruction?.trim()
      )
    );

    if (hasInvalidCheckedMedications) {
      // Optional: scroll to first error or show toast
      this.cvaNotificationService.showNotification(
        'Please fill all required fields for selected medications.',
        NotificationClassEnum.ERROR
      );
      return;
    }

    const filteredTasks = this.medications
      .map((record, index) => ({ ...record, index }))
      .filter(record =>
        record?.checked === true &&
        record?.isSaved !== true &&
        !!record?.selectedMedicine
      )
      .map(({ dosageInstruction, selectedMedicine, index }) => ({
        dosageInstruction: dosageInstruction.trim(),
        drugCode: selectedMedicine?.drugCode,
        index
      }));

    if (!filteredTasks.length) {
      return;
    }

    const payload: ISaveMedication = {
      medications: JSON.parse(JSON.stringify(filteredTasks)),
      patientId: this.patientId,
      practitionerId: this.practitionerId
    }

    this.medicationService.save(payload)
      .pipe(takeUntil(this._unsubscribeAll),
        finalize(() => this.isSaveAttempted = false)
      )
      .subscribe(({
        next: (response: IMedicationResult) => {
          response.success.forEach((index) => {
            if (this.medications[index]) {
              this.medications[index].isSaved = true;
              this.medications[index].checked = false;
              this.medications[index].hasError = false;
            }
          });
          let failedMedications: string = ""
          response.error.forEach((index) => {
            if (this.medications[index]) {
              this.medications[index].checked = false;
              this.medications[index].hasError = true;
              failedMedications = failedMedications + this.truncateString(this.medications[index].selectedMedicine?.genericName, 80) + '\n'
            }
          });

          if (response.error.length === 0) {
            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.MEDICATION_ADDED_SUCCESSFULLY,
              NotificationClassEnum.SUCCESS
            );
          } else {
            this.cvaNotificationService.showNotification(
              `${response.error.length} ${CVA_CONSTANTS.MESSAGE.MEDICATION_ADDED_ERR} \n \n ${CVA_CONSTANTS.MESSAGE.MEDICATION_ADDED_ERR_NOTE} \n ${failedMedications}`,
              NotificationClassEnum.ERROR
            );
          }

          this.medicationService.setMedications(JSON.parse(JSON.stringify(this.medications)));
        },
        error: (errorResponse: HttpErrorResponse) => {
          let errorMessage: string = '';
          if (errorResponse.error?.error) {
            errorMessage = errorResponse.error.error.errorMessage;
          }
          this.cvaNotificationService.showNotification(
            errorMessage || CVA_CONSTANTS.MESSAGE.save_err_soap_notes,
            NotificationClassEnum.ERROR
          );
        }
      }))
  }

  compareByDrugCode(a: IMedicationOption | null, b: IMedicationOption | null): boolean {
    return a?.drugCode === b?.drugCode;
  }

  scrollToBottom() {
    try {
      this.bottomRef?.nativeElement?.scrollIntoView({ behavior: 'smooth' });
    } catch {
      console.error("Error on scroll");
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
